import { jsx, jsxs } from "react/jsx-runtime";
import React__default, { useEffect } from "react";
import classNames from "classnames";
import { ZIndex, AnalyticsId, IconSize } from "../../constants.js";
import { useFocusTrap } from "../../hooks/useFocusTrap.js";
import { useIsVisible } from "../../hooks/useIsVisible.js";
import { useReturnFocusOnUnmount } from "../../hooks/useReturnFocusOnUnmount.js";
import { palette } from "../../theme/palette.js";
import { getAriaLabelAttributes } from "../../utils/accessibility.js";
import { disableBodyScroll, enableBodyScroll } from "../../utils/scroll.js";
import { u as uuid } from "../../uuid.js";
import { B as Button } from "../../Button.js";
import { C as Close } from "../../Close.js";
import { I as Icon } from "../../Icon.js";
import AlertSignFill from "../Icons/AlertSignFill.js";
import AlertSignStroke from "../Icons/AlertSignStroke.js";
import CheckOutline from "../Icons/CheckOutline.js";
import Portal from "../Portal/index.js";
import { T as Title } from "../../Title.js";
import styles from "./styles.module.scss";
var ModalVariants = /* @__PURE__ */ ((ModalVariants2) => {
  ModalVariants2["normal"] = "normal";
  ModalVariants2["warning"] = "warning";
  ModalVariants2["error"] = "error";
  ModalVariants2["success"] = "success";
  ModalVariants2["image"] = "image";
  return ModalVariants2;
})(ModalVariants || {});
var ModalSize = /* @__PURE__ */ ((ModalSize2) => {
  ModalSize2["large"] = "large";
  ModalSize2["medium"] = "medium";
  return ModalSize2;
})(ModalSize || {});
const getVariantIcon = (variant) => {
  if (variant === "error") {
    return /* @__PURE__ */ jsx(Icon, { size: IconSize.Small, svgIcon: AlertSignFill, color: palette.cherry500 });
  } else if (variant === "warning") {
    return /* @__PURE__ */ jsx(Icon, { size: IconSize.Small, svgIcon: AlertSignStroke, color: palette.black });
  } else if (variant === "success") {
    return /* @__PURE__ */ jsx(Icon, { size: IconSize.Small, svgIcon: CheckOutline, color: palette.kiwi900 });
  }
  return null;
};
const getIcon = (variant, icon) => {
  const variantIcon = getVariantIcon(variant);
  if (variantIcon) {
    return /* @__PURE__ */ jsx("div", { className: styles.modal__iconWrapper, children: variantIcon });
  }
  if (icon) {
    return /* @__PURE__ */ jsx("div", { className: styles.modal__iconWrapper, children: React__default.cloneElement(icon, {
      size: IconSize.Small
    }) });
  }
  return null;
};
const Modal = (props) => {
  var _a, _b;
  const {
    footerContent,
    variant = "normal",
    primaryButtonText = "OK",
    titleId = uuid(),
    className = "",
    size = "large",
    zIndex = ZIndex.OverlayScreen,
    role = "dialog"
  } = props;
  const topContent = React__default.useRef(null);
  const modalContentRef = React__default.useRef(null);
  const dialogRef = React__default.useRef(null);
  useFocusTrap(dialogRef, true);
  const topContentVisible = useIsVisible(topContent);
  const bottomContent = React__default.useRef(null);
  const bottomContentVisible = useIsVisible(bottomContent);
  const contentIsScrollable = modalContentRef.current && modalContentRef.current.scrollHeight > modalContentRef.current.clientHeight;
  useReturnFocusOnUnmount(dialogRef);
  function handleKeyboardEvent(e) {
    if (e.key === "Escape" && props.onClose) {
      e.stopPropagation();
      props.onClose();
    }
  }
  function handleClickEvent(event) {
    if (event.target && overlayRef.current === event.target && props.onClose) {
      event.stopPropagation();
      props.onClose();
    }
  }
  const imageView = variant === "image";
  const overlayRef = React__default.useRef(null);
  const showActions = props.secondaryButtonText && ((_a = props.secondaryButtonText) == null ? void 0 : _a.length) > 0 || props.onSuccess || footerContent;
  const ariaLabelAttributes = getAriaLabelAttributes({ label: props.ariaLabel, id: props.ariaLabelledBy, fallbackId: titleId });
  useEffect(() => {
    const overlayElement = overlayRef.current;
    disableBodyScroll();
    if (!props.disableCloseEvents && overlayElement) {
      overlayElement.addEventListener("keydown", handleKeyboardEvent);
      overlayElement.addEventListener("click", handleClickEvent);
    }
    return () => {
      enableBodyScroll();
      if (!props.disableCloseEvents && overlayElement) {
        overlayElement.removeEventListener("keydown", handleKeyboardEvent);
        overlayElement.removeEventListener("click", handleClickEvent);
      }
    };
  }, [props.disableCloseEvents]);
  useEffect(() => {
    var _a2;
    (_a2 = dialogRef.current) == null ? void 0 : _a2.focus();
  }, []);
  const dialogClasses = classNames(
    className,
    styles.modal,
    variant && styles[`modal--${variant}`],
    size && styles[`modal--${size}`],
    contentIsScrollable && !showActions && styles["modal--no-actions"]
  );
  const titleClasses = classNames({
    [styles["modal__title--error"]]: variant === "error",
    [styles["modal__title--success"]]: variant === "success"
    /* success */
  });
  const Component = /* @__PURE__ */ jsx("div", { "data-testid": "dialog-container", children: /* @__PURE__ */ jsx(
    "div",
    {
      ref: overlayRef,
      className: styles["modal-overlay"],
      "data-testid": props.testId,
      "data-analyticsid": AnalyticsId.Modal,
      style: { zIndex },
      children: /* @__PURE__ */ jsx("div", { className: styles.align, children: /* @__PURE__ */ jsxs("div", { className: dialogClasses, role, "aria-modal": "true", tabIndex: -1, ...ariaLabelAttributes, ref: dialogRef, children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: classNames(styles["modal__shadow"], styles["modal__shadow--top"], {
              [styles["modal__shadow--show"]]: !topContentVisible && contentIsScrollable
            })
          }
        ),
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: classNames(styles.modal__contentWrapper, {
              [styles["modal__contentWrapper--image"]]: imageView
            }),
            tabIndex: contentIsScrollable ? 0 : void 0,
            role: contentIsScrollable ? "region" : void 0,
            ...contentIsScrollable ? ariaLabelAttributes : {},
            ref: modalContentRef,
            children: [
              !props.noCloseButton && /* @__PURE__ */ jsx("div", { className: styles.modal__closeWrapper, children: /* @__PURE__ */ jsx("div", { className: classNames(styles.modal__closeWrapper__close), children: /* @__PURE__ */ jsx(Close, { onClick: props.onClose, ariaLabel: props.ariaLabelCloseBtn }) }) }),
              /* @__PURE__ */ jsxs(
                "div",
                {
                  className: classNames(size && styles[`modal__contentWrapper__scroll--${size}`], {
                    [styles["modal__contentWrapper__scroll--image"]]: imageView
                  }),
                  children: [
                    /* @__PURE__ */ jsx("div", { ref: topContent }),
                    /* @__PURE__ */ jsxs("div", { className: styles.modal__contentWrapper__title, children: [
                      getIcon(variant, props.icon),
                      /* @__PURE__ */ jsx(Title, { id: ariaLabelAttributes == null ? void 0 : ariaLabelAttributes["aria-labelledby"], htmlMarkup: "h3", appearance: "title3", className: titleClasses, children: props.title }),
                      props.afterTitleChildren && /* @__PURE__ */ jsx("div", { className: styles["modal__afterTitleChildren"], children: props.afterTitleChildren })
                    ] }),
                    imageView && /* @__PURE__ */ jsxs("div", { children: [
                      /* @__PURE__ */ jsx("div", { className: styles["modal__contentWrapper__imageWrapper"], children: props.children }),
                      /* @__PURE__ */ jsx("span", { className: styles["modal__contentWrapper__imageDescription"], children: props.description })
                    ] }),
                    !imageView && props.children && /* @__PURE__ */ jsx("div", { children: props.children }),
                    !imageView && !props.children && /* @__PURE__ */ jsx("p", { className: styles.modal__description, children: props.description }),
                    /* @__PURE__ */ jsx("div", { ref: bottomContent })
                  ]
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: classNames(styles["modal__shadow"], styles["modal__shadow--bottom"], {
              [styles["modal__shadow--show"]]: !bottomContentVisible && contentIsScrollable
            })
          }
        ),
        showActions && /* @__PURE__ */ jsxs("div", { className: classNames(styles["modal__call-to-action"], size && styles[`modal__call-to-action--${size}`]), children: [
          props.onSuccess && primaryButtonText && /* @__PURE__ */ jsx(Button, { onClick: props.onSuccess, children: primaryButtonText }),
          props.secondaryButtonText && ((_b = props.secondaryButtonText) == null ? void 0 : _b.length) > 0 && /* @__PURE__ */ jsx(Button, { variant: "borderless", onClick: props.onClose, children: props.secondaryButtonText }),
          footerContent
        ] })
      ] }) })
    }
  ) });
  if (props.printable) {
    const printModal = "print-modal";
    return /* @__PURE__ */ jsxs(Portal, { className: printModal, testId: "print-modal", children: [
      /* @__PURE__ */ jsx("style", { media: "print", children: `body > *:not(.${printModal}) {display: none;}` }),
      Component
    ] });
  }
  return Component;
};
export {
  ModalSize,
  ModalVariants,
  Modal as default
};
//# sourceMappingURL=index.js.map
